import * as React from "react";
import { makeDialogDraggable } from 'dialog-draggable';

makeDialogDraggable();

let nonModal = false;

export type ComponentFactory = (
    onSubmit: (d: any) => void,
    onClose: () => void) => React.ReactNode

export type DialogArguments = {
    factory: ComponentFactory,
    size?: "sm" | "md" | "xl" | "lg" | "recorder",
    className?: string,
    nonModal?: boolean
}

let dialogOpen = false;
let dialogCloseResolvers: (() => void)[] = [];

export const isDialogOpen = () => dialogOpen;

// Resolves when the current dialog unmounts (immediately if none is open).
// Use this when you need to open a dialog but one might already be showing.
export const onNextDialogClose = (): Promise<void> => {
    if (!dialogOpen) return Promise.resolve();
    return new Promise<void>(resolve => dialogCloseResolvers.push(resolve));
};

type DialogOutcome = { type: 'resolve'; val: any } | { type: 'reject' };

// Sentinel: fires onUnmount when the dialog element is removed from the DOM.
// Sentinel that fires onUnmount only on real unmount, not Strict Mode's fake one.
// Strict Mode runs cleanup then immediately remounts — componentMountedRef goes false→true
// before the microtask fires, so the callback is suppressed.
function DialogMountTracker({ onUnmount }: { onUnmount: () => void }) {
    const onUnmountRef = React.useRef(onUnmount);
    onUnmountRef.current = onUnmount;
    const componentMountedRef = React.useRef(false);
    React.useEffect(() => {
        componentMountedRef.current = true;
        return () => {
            componentMountedRef.current = false;
            queueMicrotask(() => {
                if (!componentMountedRef.current) {
                    onUnmountRef.current();
                }
            });
        };
    }, []);
    return null;
}

const DialogServiceContext = React.createContext<
    (args: DialogArguments) => Promise<any>>(Promise.reject);

export const useDialog = () =>
    React.useContext(DialogServiceContext);

export const DialogServiceProvider = ({ children }: { children: React.ReactNode }) => {
    const [
        dialogComponent,
        setDialogComponent
    ] = React.useState<React.ReactNode | null>(null);

    const awaitingPromiseRef = React.useRef<{
        resolve: (val: any) => void;
        reject: () => void;
    }>({ resolve: () => {}, reject: () => {} });

    const dialog = React.useRef<HTMLDialogElement | undefined>(undefined);

    // Stores whether the dialog was submitted or closed, captured at close time
    // so the onUnmount closure reads the correct value after unmount.
    const outcomeRef = React.useRef<DialogOutcome>({ type: 'reject' });

    const setRefAndOpenDialog = React.useCallback((node: HTMLDialogElement) => {
        if (node) {
            if (nonModal) {
                node.show();
            } else {
                node.showModal();
            }
            dialog.current = node;
            node.addEventListener("cancel", handleClose);
        }
    }, []);

    const openDialog = (args: DialogArguments) => {

        if (dialogOpen) {
            return Promise.reject(new Error("Already in use"));
        }

        nonModal = args.nonModal === true;
        outcomeRef.current = { type: 'reject' };

        const element = <dialog ref={setRefAndOpenDialog} className={[args.className, args.size || "md"].join(" ")}>
            <DialogMountTracker onUnmount={() => {
                // Order matters: dialogOpen = false before settling the promise,
                // so any .then()/.catch() handler can immediately open a new dialog.
                dialogOpen = false;
                if (outcomeRef.current.type === 'resolve') {
                    awaitingPromiseRef.current.resolve(outcomeRef.current.val);
                } else {
                    awaitingPromiseRef.current.reject();
                }
                const resolvers = dialogCloseResolvers.splice(0);
                resolvers.forEach(r => {r();});
            }} />
            {args.factory(handleSubmit, handleClose)}
        </dialog>;

        setDialogComponent(element);
        dialogOpen = true;

        return new Promise<any>((resolve, reject) => {
            awaitingPromiseRef.current = { resolve, reject };
        });
    };

    const closeDialog = () => {
        dialog.current?.removeEventListener("cancel", handleClose);
        dialog.current?.close();
        setDialogComponent(null);
    };

    const handleClose = () => {
        outcomeRef.current = { type: 'reject' };
        closeDialog();
    };

    const handleSubmit = (val: any) => {
        outcomeRef.current = { type: 'resolve', val };
        closeDialog();
    };

    return (
        <>
            <DialogServiceContext.Provider
                value={openDialog}
                children={children}
            />
            {dialogComponent}
        </>
    );
};
